'use client';

import { useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiPauseCircleLine, RiRepeat2Line, RiScales3Line, RiSearchEyeLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { RiCornerDownRightLine } from '@/components/ui/rtl-icons';
import { useInlineSign } from '@/i18n/direction';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Ne sorabilirsiniz" — gösterimin hemen altındaki gri bölüm.
 *
 * Üstteki bölüm üç soruyu kendi eliyle denetti; buradaki soru artık "peki
 * BAŞKA ne sorabilirim". Cevabı bir yetenek listesi değil, insanların
 * gerçekten yazdığı cümleler: ajanla konuşmanın öğrenilecek bir dili
 * olmadığını ancak cümlenin kendisi gösterebilir.
 *
 * ── Dört başlık neden sekme ───────────────────────────────────────────────
 *
 * On iki cümle alt alta dizilseydi göz hepsini tek bir liste olarak okur ve
 * aralarındaki asıl fark kaybolurdu: bazıları okumayla biter, bazıları onay
 * kartı doğurur, bazıları Operatör'e görev olur. Sekme o farkı yapının
 * kendisine taşıyor — her başlığın altındaki "ne olur" cümlesi, o gruptaki
 * soruların ortak sonucu.
 *
 * ── Erişilebilirlik ───────────────────────────────────────────────────────
 *
 * Gerçek sekme örüntüsü: `role="tablist"` + gezinen odak (yalnızca etkin
 * sekme `tabIndex=0`), ok tuşları, Home/End. Panel `role="tabpanel"` ve
 * odaklanabilir — içeriği klavyeyle okunabilsin diye.
 *
 * Kayan gösterge `layoutId` ile: sekme değişince çubuk yeni sekmeye GİDİYOR,
 * orada belirmiyor. Hareket burada "aynı gösterge yer değiştirdi" diyor
 * (DESIGN.md §4); azaltılmış hareket tercihinde kök `<MotionConfig>` onu
 * anlık yapıyor, bilgi kaybolmuyor — durum ayrıca `aria-selected` ve
 * mürekkep farkıyla da yazılı.
 */

const GROUPS = ['understand', 'compare', 'act', 'automate'] as const;
type Group = (typeof GROUPS)[number];

const ICON: Record<Group, typeof RiSearchEyeLine> = {
  understand: RiSearchEyeLine,
  compare: RiScales3Line,
  act: RiPauseCircleLine,
  automate: RiRepeat2Line,
};

/** Her gruptaki cümle sayısı sabit; sıra = listede göründüğü sıra. */
const LINES = ['q1', 'q2', 'q3'] as const;

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const copyV: Variants = {
  hidden: { opacity: 0, y: 10, filter: 'blur(5px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
};

const panelV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: 0.04 } },
};

/* Cümleler blur'suz ve kısa yollu: aynı anda dört parça giriyor ve panel her
   sekme değişiminde yeniden oynuyor — ağır bir giriş burada yorucu olurdu. */
const lineV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
  exit: { opacity: 0, transition: { duration: dur.instant } },
};

export function AgentAsks() {
  const t = useTranslations('featuresAgent.asks');
  const uid = useId();
  /* Sekme şeridi `dir`i izliyor: sağdan sola dilde "sonraki" ← ile. */
  const sign = useInlineSign();

  const [open, setOpen] = useState<Group>('understand');
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (i: number) => {
    const next = (i + GROUPS.length) % GROUPS.length;
    setOpen(GROUPS[next] as Group);
    tabs.current[next]?.focus();
  };

  const onKey = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowRight') move(i + sign);
    else if (e.key === 'ArrowLeft') move(i - sign);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(GROUPS.length - 1);
    else return;
    e.preventDefault();
  };

  const Icon = ICON[open];

  return (
    <section className="asks" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="asks-inner">
            <motion.div className="asks-head" variants={headV} initial={false} animate="show">
              <motion.p className="asks-eyebrow" variants={copyV}>
                {t('eyebrow')}
              </motion.p>
              <motion.h2 className="asks-title" id={`${uid}-title`} variants={copyV}>
                {t.rich('title', { hl })}
              </motion.h2>
              <motion.p className="asks-lead" variants={copyV}>
                {t('lead')}
              </motion.p>
            </motion.div>

            <motion.div
              className="asks-box"
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={springFade(spring.soft, dur.base)}
            >
              <div className="asks-tabs" role="tablist" aria-label={t('eyebrow')}>
                {GROUPS.map((key, i) => {
                  const on = key === open;
                  return (
                    <button
                      key={key}
                      ref={(el) => {
                        tabs.current[i] = el;
                      }}
                      type="button"
                      role="tab"
                      id={`${uid}-tab-${key}`}
                      aria-selected={on}
                      aria-controls={`${uid}-panel`}
                      tabIndex={on ? 0 : -1}
                      className={`asks-tab${on ? ' asks-tab--on' : ''}`}
                      onClick={() => setOpen(key)}
                      onKeyDown={(e) => onKey(e, i)}
                    >
                      {t(`groups.${key}.name`)}
                      {on && (
                        <motion.span
                          className="asks-tab-bar"
                          layoutId={`${uid}-bar`}
                          transition={spring.default}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              <div
                className="asks-panel"
                role="tabpanel"
                id={`${uid}-panel`}
                aria-labelledby={`${uid}-tab-${open}`}
                tabIndex={0}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={open}
                    className="asks-panel-grid"
                    variants={panelV}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                  >
                    <ul className="asks-list">
                      {LINES.map((line) => (
                        <motion.li key={line} className="asks-line" variants={lineV}>
                          <RiCornerDownRightLine
                            className="asks-line-mark"
                            size={16}
                            aria-hidden="true"
                          />
                          <span className="asks-line-text">{t(`groups.${open}.${line}`)}</span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* Grubun ortak sonucu. Cümlelerden AYRI bir mürekkepte:
                        yukarısı kullanıcının yazdığı şey, burası olacak şey. */}
                    <motion.p className="asks-what" variants={lineV}>
                      <Icon size={17} aria-hidden="true" />
                      <span>{t(`groups.${open}.what`)}</span>
                    </motion.p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
